import React, { useState, useRef, useCallback } from 'react';
import block from 'propmods';
import { useDropzone } from 'react-dropzone';
import { nanoid } from 'nanoid/non-secure';
import { ThereminvoxSoundMeta, InnerSoundMeta, ThereminvoxTestingModeProps } from '../../model/thereminvox';
import Description from '../Form/Description/Description';
import { Skill } from '../../model/skill';
import UploadIcon from '../../icons/upload.svg';
import { Field } from '../Form/Form';
import Toggle from '../Toggle/Toggle';
import { ThereminvoxSoundsContainer } from './ThereminnvoxSoundsContainer';
import StationMiniPic from './assets/station-mini-pic.svg';
import { useInnerSoundsMeta, stopAllSoundsIn } from '../../utils/thereminvox';
import { isTouchSupported } from '../../utils';
import * as keyset from 'i18n/Thereminvox';
import i18n from '@yandex-int/i18n';

import './ThereminvoxSounds.scss';

const b = block('ThereminvoxSounds');
const t = i18n(keyset);

const ThereminvoxSoundsDescription: React.FC<{ error?: boolean }> = ({ error }) => {
    const allowedSoundsFragment = <>{t('sounds-format')}</>;

    return (
        <div {...b('description')}>
            <Description offsetTop={false}>
                {t('description-sounds')}
                <br />
                {error ? <span {...b('error')}>{allowedSoundsFragment}</span> : <span>{allowedSoundsFragment}</span>}
            </Description>
        </div>
    );
};

interface ThereminvoxSoundsProps extends Omit<ThereminvoxTestingModeProps, 'stopAllSounds'> {
    skill: Skill;
    soundsMeta: ThereminvoxSoundMeta[];
    onSoundsMetaChange: (soundsMeta: ThereminvoxSoundMeta[]) => void;
    disabled?: boolean;
}

export const ThereminvoxSounds: React.FC<ThereminvoxSoundsProps> = ({
    skill,
    soundsMeta,
    onSoundsMetaChange,
    disabled,
    ...testingModeProps
}) => {
    const [isTestingMode, setIsTestingMode] = useState(false);
    const [isClientError, setIsClientError] = useState(false);
    const controlsRef = useRef<HTMLDivElement>(null);
    const soundsContainerRef = useRef<HTMLDivElement>(null);
    const { innerSoundsMeta, setInnerSoundsMeta, hasUploadingSounds, allUploaded, hasMaxSounds, hasErrors } =
        useInnerSoundsMeta(soundsMeta, onSoundsMetaChange);

    const onDrop = async (acceptedFiles: File[], rejectedFiles: File[]) => {
        setIsClientError(false);

        if (rejectedFiles.length) {
            setIsClientError(true);
            return;
        }

        const acceptingCount = __appConfig.thereminvox.maxSounds - innerSoundsMeta.length;

        const appendedInnerMeta = acceptedFiles.slice(0, acceptingCount).map((file) => ({
            state: 'uploading',
            file,
            id: nanoid(),
        })) as InnerSoundMeta[];

        setInnerSoundsMeta([...innerSoundsMeta, ...appendedInnerMeta]);
    };

    const isDropzoneDisabled = isTestingMode || hasMaxSounds || hasUploadingSounds || disabled;
    const isTestingDisabled = !allUploaded || hasErrors || disabled || innerSoundsMeta.length === 0;
    const displayTestingModeToggle =
        soundsMeta.length !== 0 && !__appConfig.isMobileSafari && Boolean(document.elementsFromPoint);

    const stopAllSounds = () => stopAllSoundsIn({ container: soundsContainerRef.current! });

    const onSoundContainerMouseLeave = useCallback(() => {
        if (testingModeProps.stopOnCeil && isTestingMode) {
            stopAllSounds();
        }
    }, [testingModeProps.stopOnCeil, isTestingMode]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        minSize: 1,
        maxSize: __appConfig.thereminvox.maxSoundSize,
        onDrop,
        accept: ['audio/mpeg', 'audio/mp3'],
        disabled: isDropzoneDisabled,
    });

    return (
        <div {...b()}>
            <Field label={t('label-set')} required>
                <ThereminvoxSoundsDescription error={isClientError} />
                <div {...b('controls')} ref={controlsRef}>
                    <div {...getRootProps()} {...b('dropzone', { isDragActive, disabled: isDropzoneDisabled })}>
                        <input {...getInputProps()} />
                        <div {...b('upload-icon')}>
                            <UploadIcon />
                        </div>
                        <div {...b('upload-label')}>{t('select-sounds')}</div>
                    </div>
                    {displayTestingModeToggle && (
                        <div {...b('toggle')}>
                            <label {...b('toggle-label')}>
                                <span {...b('toggle-label-text', { disabled: isTestingDisabled })}>{t('testing')}</span>
                                <Toggle
                                    icons={false}
                                    disabled={isTestingDisabled}
                                    checked={isTestingMode}
                                    onChange={(e) => {
                                        setIsClientError(false);
                                        setIsTestingMode(e.currentTarget.checked);
                                        stopAllSounds();

                                        if (e.currentTarget.checked && isTouchSupported()) {
                                            controlsRef.current!.scrollIntoView({
                                                behavior: 'smooth',
                                                block: 'start',
                                            });
                                        }
                                    }}
                                />
                            </label>
                        </div>
                    )}
                </div>
                {innerSoundsMeta.length > 0 && (
                    <div
                        {...b('sounds')}
                        ref={soundsContainerRef}
                        onMouseLeave={onSoundContainerMouseLeave}
                        style={{
                            marginTop: `${innerSoundsMeta.length * 8}px`,
                        }}
                    >
                        <ThereminvoxSoundsContainer
                            innerSoundsMeta={innerSoundsMeta}
                            setInnerSoundsMeta={setInnerSoundsMeta}
                            skill={skill}
                            isTestingMode={isTestingMode}
                            stopAllSounds={stopAllSounds}
                            {...testingModeProps}
                        />
                        <div {...b('station-mini-pic')}>
                            <div {...b('station-mini-ellipse-icon')} />
                            <StationMiniPic />
                        </div>
                    </div>
                )}
            </Field>
        </div>
    );
};
